import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import {isEqual as _isEqual} from 'lodash';
import React, {
  memo,
  type MutableRefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import {type ValueOp} from '../../../util/filterTypes';
import {getValue} from '../../../util/runs';
import {
  type Key as RunKey,
  type Value as RunValue,
} from '../../../util/runTypes';
import {rowIsRun} from '../../../util/tablecols';
import {type TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {type WBTableColumn, type WBTableRowInterface} from './types';

interface WBTableCellProps {
  className: string;
  style?: React.CSSProperties;
  column: WBTableColumn;
  row: WBTableRowInterface;
  loadingChildren: boolean;
  recursionDepth: number;
  cellHoverProps: {
    onMouseEnter(): void;
    onMouseLeave(): void;
  };
  isHoveringRow: boolean;
  isHoveringColumn: boolean;
  isGroup: boolean;

  // Props below are used to trigger rerenders
  isGroupExpanded: boolean;
  isSingleMode?: boolean;
  showArtifactCounts?: boolean;
  showLogCounts?: boolean;

  expanded?: boolean;
  truncationType: TruncationType;
  addFilter(key: RunKey, op: ValueOp, value: RunValue): void;
  toggleExpandedRow(rowAddress: string): void;
}

const WBTableCellComp: React.FC<WBTableCellProps> = ({
  className,
  style,
  column,
  row,
  loadingChildren,
  recursionDepth,
  cellHoverProps,
  isHoveringRow,
  isHoveringColumn,
  isGroup,
  isGroupExpanded,
  addFilter,
  toggleExpandedRow,
  truncationType,
}) => {
  // TODO(react-compiler): column.renderCell is a RunSelectorInner class method
  // that reads this.props, so its output is not a pure function of these
  // props. Compiled output would cache a stale cell; the memo comparator below
  // already decides when to re-render.
  'use no memo';
  const [changeAnimating, setChangeAnimating] = useState<boolean>(false);
  const ref: MutableRefObject<HTMLDivElement | null> =
    useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (ref.current == null) {
      return;
    }
    ref.current.addEventListener('animationend', () => {
      setChangeAnimating(false);
    });
  }, []);

  return (
    <div
      ref={ref}
      style={style}
      className={
        className + (changeAnimating ? ' highlight-change-animation' : '')
      }
      // TODO: debounce this if necessary for performance
      onMouseEnter={cellHoverProps.onMouseEnter}
      onMouseLeave={cellHoverProps.onMouseLeave}>
      {column.renderCell
        ? column.renderCell(column, row, {
            addFilter,
            toggleExpandedRow,
            recursionDepth,
            isGroup,
            isGroupExpanded,
            loadingChildren,
            isHoveringRow,
            isHoveringColumn,
            truncationType,
          })
        : (getValue(row as any, column.key) as React.ReactNode)}
    </div>
  );
};

export const WBTableCell = memo(WBTableCellComp, (prevProps, nextProps) => {
  if (rowIsRun(prevProps.row) && rowIsRun(nextProps.row)) {
    if (
      prevProps.row.state !== nextProps.row.state ||
      prevProps.row.groupCounts !== nextProps.row.groupCounts
    ) {
      return false;
    }

    if (
      nextProps.column.key.section === 'run' &&
      !_isEqual(
        prevProps.row.multiValueMetadataColumns?.[nextProps.column.key.name],
        nextProps.row.multiValueMetadataColumns?.[nextProps.column.key.name]
      )
    ) {
      return false;
    }

    if (nextProps.column.key.name === 'notes') {
      // upsertRun requires both displayName and notes to be provided,
      // so the notes cell must always have the latest/correct displayName
      // or else we could persist a stale displayName after editing notes
      const isNotesChanged =
        prevProps.row.displayName !== nextProps.row.displayName ||
        prevProps.row.notes !== nextProps.row.notes;
      const isDisplayNameChanged =
        prevProps.row.displayName !== nextProps.row.displayName;
      if (isNotesChanged || isDisplayNameChanged) {
        return false;
      }
    }
    // The displayName column renders RunSelectorRowHeader, whose visibility
    // checkbox reads row state beyond the column's primary value (e.g.
    // row.summary for resolving a brushed PC-axis metric). The valChanged
    // check below only inspects column.key, so without this we'd memoize on
    // a stale row when summary metrics land after a brush-triggered refetch
    // and the eyeball wouldn't update until something else forced a render.
    if (
      nextProps.column.key.section === 'run' &&
      nextProps.column.key.name === 'displayName' &&
      prevProps.row !== nextProps.row
    ) {
      return false;
    }
  }
  const valChanged = !_isEqual(
    // Completely boneheaded:
    getValue(nextProps.row as any, nextProps.column.key),
    getValue(prevProps.row as any, prevProps.column.key)
  );
  if (valChanged) {
    return false;
  }
  const propsAreEqual = makePropsAreEqual({
    name: 'WBTableCell',
    deep: ['columnKey', 'column'],
    ignore: ['row', 'cellHoverProps'],
    ignoreFunctions: true,
    debug: false,
    verbose: true,
  });
  return propsAreEqual(prevProps, nextProps);
});
